import { PwInput } from "@/components/ui/pw-input";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { LogOut, PackageCheck, PlusCircle, FileSpreadsheet, ListChecks, KeyRound, MessageSquare, Camera, Bell, Users, Gauge, Trophy, Megaphone, Store, Tag } from "lucide-react";
import { toast } from "sonner";
import { papi, getToken, setToken, errText } from "./portalApi";
import { PortalLogin } from "./PortalLogin";
import { PortalOrderForm } from "./PortalOrderForm";
import { PortalUpload } from "./PortalUpload";
import { PortalParcels } from "./PortalParcels";
import { PortalQueries } from "./PortalQueries";
import { PortalChanges } from "./PortalChanges";
import { PortalPhotoRequests } from "./PortalPhotoRequests";
import { PortalClients } from "./crm/PortalClients";
import PortalTrackPage from "./PortalTrackPage";
import { PerfDashboard } from "./perf/PerfDashboard";
import { LeaderOverview } from "./leader/LeaderOverview";
import { LeaderClients, LeaderParcels } from "./leader/LeaderViews";
import { LeaderNotes, PartnerLeaderNotes } from "./leader/LeaderNotes";
import { KitVersionBadge } from "./KitVersionBadge";
import { LeaderWeekly } from "./leader/LeaderWeekly";
import { NewsBanner, PortalNews } from "./news/PortalNews";
import { PortalDropship } from "./dropship/PortalDropship";
import { PortalPriceList } from "./pricelist/PortalPriceList";
import { PriceChangeNotice } from "./pricelist/PriceChangeNotice";

const RIGHT_OF_TAB = { clients: "tab_clients", photos: "tab_photos" };
const TABS = [["parcels", "My parcels", ListChecks], ["new", "New order", PlusCircle], ["upload", "Upload Excel", FileSpreadsheet], ["queries", "My queries", MessageSquare], ["photos", "Product photos", Camera], ["clients", "My clients", Users], ["dropship", "Dropship clients", Store], ["pricelist", "Price list", Tag], ["news", "News", Megaphone]];
const LEADER_TABS = [["team", "Team performance", Gauge], ["lparcels", "Team parcels", ListChecks], ["lclients", "Team clients", Users], ["lnotes", "Follow-up notes", MessageSquare], ["news", "News", Megaphone]];

const ChangePassword = ({ onClose }) => {
  const [f, setF] = useState({ old_password: "", new_password: "" });
  const save = async () => {
    try { await papi("post", "/change-password", f); toast.success("Password changed"); onClose(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="portal-change-pw">
      <div className="w-full max-w-xs space-y-3 rounded-2xl bg-white p-5">
        <b>Change password</b>
        <PwInput placeholder="Current password" value={f.old_password} onChange={(e) => setF({ ...f, old_password: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm" data-testid="portal-pw-old" />
        <PwInput placeholder="New password (6+ characters)" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="w-full rounded-lg border px-3 py-2 text-sm" data-testid="portal-pw-new" />
        <div className="flex justify-end gap-2"><button onClick={onClose} className="rounded-lg border px-3 py-1.5 text-sm">Cancel</button>
          <button onClick={save} className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white" data-testid="portal-pw-save">Save</button></div>
      </div>
    </div>
  );
};

export default function PortalApp({ withToaster = true }) {
  const [me, setMe] = useState(null);
  const [checked, setChecked] = useState(false);
  const [tab, setTab] = useState("parcels");
  const [reload, setReload] = useState(0);
  const [pw, setPw] = useState(false);
  const [qWait, setQWait] = useState(0);
  const [prNew, setPrNew] = useState(0);
  const [crmN, setCrmN] = useState(0);
  const [prefill, setPrefill] = useState(null);
  const [perf, setPerf] = useState(null);
  const [lcode, setLcode] = useState("");
  const [lDue, setLDue] = useState(0);
  const [lWeek, setLWeek] = useState(null);
  const [news, setNews] = useState({ unseen: 0, banner: [] });
  const [pkDone, setPkDone] = useState([]);
  // Strip sizes the office added for products this partner asked about: show once, then clear
  const showPkDone = () => {
    if (!pkDone.length) return;
    toast.success(`Strip size added for ${pkDone.length} product${pkDone.length > 1 ? "s" : ""} you asked about`,
      { description: pkDone.map((x) => `${x.medicine} — strip of ${x.packing} added`).join("\n"), duration: 10000 });
    papi("post", "/packing/done/seen").catch(() => {});
    setPkDone([]);
  };
  const newsPoll = () => papi("get", "/news").then(setNews).catch(() => {});
  const [plCh, setPlCh] = useState(null);
  const [plChanged, setPlChanged] = useState(false);
  useEffect(() => { if (me?.pricelist && me.kind !== "leader") papi("get", "/pricelist/changes").then(setPlCh).catch(() => {}); }, [me]);
  useEffect(() => {
    if (!me) return undefined;
    newsPoll();
    const t = setInterval(newsPoll, 60000);
    return () => clearInterval(t);
  }, [me]);
  const leaderPoll = () => Promise.all([papi("get", "/leader/notes").catch(() => ({})), papi("get", "/leader/digest").catch(() => ({}))])
    .then(([n, d]) => { setLDue((n.due || 0) + (d.digest ? 1 : 0)); setLWeek(d.digest || null); });
  const crmPoll = () => papi("get", "/crm/alerts").then((r) => setCrmN(r.count || 0)).catch(() => {});
  useEffect(() => {
    if (!me) return;
    if (me.kind === "leader") { setTab("team"); leaderPoll(); return; }
    if (me.rights?.perf_popup !== false) papi("get", "/perf").then((d) => d.show_today && setPerf(d)).catch(() => {});
  }, [me]);
  useEffect(() => { if (me && me.kind !== "leader") papi("get", "/queries").then((r) => setQWait(r.waiting_you || 0)).catch(() => {}); }, [me, tab]);
  useEffect(() => {
    if (!me || me.kind === "leader") return undefined;
    const poll = () => {
      if (me.rights?.tab_photos !== false) papi("get", "/photo-requests").then((r) => setPrNew(r.unseen || 0)).catch(() => {});
      papi("get", "/queries").then((r) => setQWait(r.waiting_you || 0)).catch(() => {});
      if (me.rights?.tab_clients !== false) crmPoll();
      papi("get", "/packing/done").then((r) => setPkDone(r.rows || [])).catch(() => {});
    };
    poll(); const t = setInterval(poll, 60000);
    window.addEventListener("portal-queries-changed", poll);
    return () => { clearInterval(t); window.removeEventListener("portal-queries-changed", poll); };
  }, [me]);
  useEffect(() => {
    document.title = "WINNERSTEAM Portal";
    const out = () => setMe(null);
    window.addEventListener("portal-logout", out);
    if (getToken()) papi("get", "/me").then(setMe).catch(() => {}).finally(() => setChecked(true));
    else setChecked(true);
    return () => window.removeEventListener("portal-logout", out);
  }, []);
  const logout = () => { setToken(""); setMe(null); setQWait(0); setPrNew(0); setCrmN(0); setPerf(null); setPkDone([]); setTab("parcels"); };
  if (window.location.pathname.startsWith("/t/")) return <PortalTrackPage />;
  if (!checked) return null;
  if (!me) return <>{withToaster && <Toaster richColors position="bottom-center" />}<PortalLogin onIn={() => papi("get", "/me").then(setMe)} /></>;
  const done = () => { setReload((k) => k + 1); setPrefill(null); setTab("parcels"); };
  const reorder = (c, h) => { setPrefill({ customer_name: c.name, address: c.addresses[0] || "", mode: h.mode || "EMS", items: h.items.map((i) => ({ medicine: i.medicine, quantity: i.quantity })) }); setTab("new"); };
  const bellN = prNew + qWait + crmN + pkDone.length;
  const leader = me.kind === "leader";
  const tabs = leader ? LEADER_TABS : TABS.filter(([k]) => (!RIGHT_OF_TAB[k] || me.rights?.[RIGHT_OF_TAB[k]] !== false) && (k !== "dropship" || me.dropship) && (k !== "pricelist" || me.pricelist));
  return (
    <div className="min-h-screen bg-[#f3f5f4]" data-testid="portal-app">
      {withToaster && <Toaster richColors position="bottom-center" />}
      <header className="sticky top-0 z-20 bg-[#0f1c2e] text-white">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <PackageCheck className="h-6 w-6 text-teal-400" />
          <div className="min-w-0"><div className="truncate text-sm font-bold" data-testid="portal-partner-name">{me.name}</div><div className="text-[11px] text-slate-300">{me.codes.join(" · ")}</div></div>
          {!leader && <button onClick={() => { const only = pkDone.length && !qWait && !crmN && !prNew; showPkDone(); if (!only) setTab(qWait ? "queries" : crmN ? "clients" : "photos"); }} className="relative ml-auto rounded-lg p-2 hover:bg-white/10"
            title={[qWait && `${qWait} team repl${qWait > 1 ? "ies" : "y"} waiting for you`, prNew && `${prNew} product photo request(s) answered`, crmN && `${crmN} client reminder(s)`, pkDone.length && `${pkDone.length} strip size(s) added`].filter(Boolean).join(" · ") || "Notifications"} data-testid="portal-bell">
            <Bell className={`h-4 w-4 ${bellN ? "animate-pulse text-amber-300" : ""}`} />
            {bellN > 0 && <span className="absolute -right-0.5 -top-0.5 rounded-full bg-rose-500 px-1 text-[10px] font-bold" data-testid="portal-bell-count">{bellN}</span>}</button>}
          {leader && <button onClick={() => setTab("lnotes")} className="relative ml-auto rounded-lg p-2 hover:bg-white/10" title={lDue ? `${lDue} follow-up reminder(s) due` : "Follow-up notes"} data-testid="leader-bell">
            <Bell className={`h-4 w-4 ${lDue ? "animate-pulse text-amber-300" : ""}`} />
            {lDue > 0 && <span className="absolute -right-0.5 -top-0.5 rounded-full bg-rose-500 px-1 text-[10px] font-bold" data-testid="leader-bell-count">{lDue}</span>}</button>}
          {!leader && me.rights?.perf_popup !== false && <button onClick={() => papi("get", "/perf").then(setPerf).catch(() => {})} className="rounded-lg p-2 hover:bg-white/10" title="My performance" data-testid="portal-perf-btn"><Trophy className="h-4 w-4 text-amber-300" /></button>}
          <button onClick={() => setPw(true)} className="rounded-lg p-2 hover:bg-white/10" title="Change password" data-testid="portal-change-pw-btn"><KeyRound className="h-4 w-4" /></button>
          <button onClick={logout} className="rounded-lg p-2 hover:bg-white/10" title="Sign out" data-testid="portal-logout"><LogOut className="h-4 w-4" /></button>
        </div>
        <nav className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-3 pb-2">
          {tabs.map(([k, l, I]) => (
            <button key={k} onClick={() => { if (k === "new") setPrefill(null); if (k === "pricelist") setPlChanged(false); setTab(k); }} className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-teal-600 text-white" : "text-slate-300 hover:bg-white/10"}`} data-testid={`portal-tab-${k}`}>
              <I className="h-4 w-4" /> {l}
              {k === "queries" && qWait > 0 && <span className="animate-pulse rounded-full bg-fuchsia-500 px-1.5 text-[11px] text-white" data-testid="portal-queries-badge">{qWait}</span>}
              {k === "news" && news.unseen > 0 && <span className="animate-pulse rounded-full bg-teal-500 px-1.5 text-[11px] text-white" data-testid="portal-news-badge">{news.unseen}</span>}
              {k === "clients" && crmN > 0 && <span className="animate-pulse rounded-full bg-amber-500 px-1.5 text-[11px] text-white" data-testid="portal-clients-badge">{crmN}</span>}
              {k === "photos" && prNew > 0 && <span className="animate-pulse rounded-full bg-rose-500 px-1.5 text-[11px] text-white" data-testid="portal-photos-badge">{prNew}</span>}</button>))}
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-3 py-4 md:px-4 md:py-6">
        {!leader && <PortalChanges />}
        {tab !== "news" && <NewsBanner items={news.banner} onOpen={() => setTab("news")} onChanged={newsPoll} />}
        {tab === "news" && <PortalNews onSeen={newsPoll} />}
        {tab === "dropship" && me.dropship && <PortalDropship changes={plCh} margins={!!me.pricelist} />}
        {tab === "pricelist" && me.pricelist && <PortalPriceList key={plChanged ? "ch" : "all"} me={me} initialChanged={plChanged} />}
        {!leader && tab === "parcels" && me.pricelist && <PriceChangeNotice data={plCh} onOpen={() => { setPlChanged(true); setTab("pricelist"); }} />}
        {!leader && tab === "parcels" && <PartnerLeaderNotes />}
        {tab === "lnotes" && leader && <LeaderNotes me={me} onDue={leaderPoll} />}
        {tab === "team" && leader && lWeek && <LeaderWeekly d={lWeek} onClose={() => papi("post", "/leader/digest/seen").finally(leaderPoll)} />}
        {tab === "team" && leader && <LeaderOverview me={me} onCode={(c) => { setLcode(c); setTab("lparcels"); }} />}
        {tab === "lparcels" && leader && <LeaderParcels key={lcode} me={me} initialCode={lcode} />}
        {tab === "lclients" && leader && <LeaderClients me={me} />}
        {tab === "parcels" && !leader && <PortalParcels me={me} reloadKey={reload} />}
        {tab === "new" && <PortalOrderForm key={prefill ? JSON.stringify(prefill) : "blank"} me={me} initial={prefill} onDone={done} />}
        {tab === "upload" && <PortalUpload me={me} onDone={done} />}
        {tab === "queries" && <PortalQueries onCount={setQWait} />}
        {tab === "photos" && me.rights?.tab_photos !== false && <PortalPhotoRequests onUnseen={setPrNew} />}
        {tab === "clients" && me.rights?.tab_clients !== false && <PortalClients onReorder={reorder} onAlerts={crmPoll} />}
      </main>
      {pw && <ChangePassword onClose={() => setPw(false)} />}
      {perf && <PerfDashboard data={perf} onClose={() => setPerf(null)} />}
      <KitVersionBadge />
    </div>
  );
}
